import { MapViewModel } from '../model/MapViewModel.ts';
import { getState } from '../../support/helpers/states.ts';
import { IAddress } from '../../support/model/IAddress.ts';
import { ILocation } from '../../support/model/ILocation.ts';
import { ISuggestion } from '../../support/model/ISuggestion.ts';
import { ICoordinate } from '../../support/model/ICoordinate.ts';
import { EnvConfiguration } from '../../app/config/EnvConfiguration.ts';

const INCLUDED_CATEGORIES = [
    'airport',
    'beach',
    'bridge',
    'campground',
    'canal',
    'church',
    'dog_park',
    'education',
    'factory',
    'field',
    'forest',
    'golf_course',
    'historic_site',
    'island',
    'lake',
    'lighthouse',
    'marina',
    'monument',
    'mountain',
    'museum',
    'nature_reserve',
    'park',
    'pier',
    'playground',
    'racetrack',
    'railway_station',
    'river',
    'ski_area',
    'sports_center',
    'stadium',
    'swimming_pool',
    'tennis_courts',
    'theater',
    'theme_park',
    'tourist_attraction',
    'tunnel',
    'viewpoint',
    'vineyard',
    'warehouse',
    'waterfall',
    'windmill',
    'winery',
    'zoo',
];

const placeTypeParsers: { [key: string]: (place_name: string) => IAddress} = {
    'country': (addressText: string = ''): IAddress => {
        return { city: '', state: '', country: (addressText || '').trim() };
    },
    'address': (addressText: string = ''): IAddress => {
        // eg 12025 Catamount Drive, Leicester, Vermont 05733, United States
        const parts = addressText.split(',');
        const country = (parts.pop() || '').trim();
        const stateZip = (parts.pop() || '').trim();
        const city = (parts.pop() || '').trim();
        const street = parts.join(',').trim();

        const stateZipParts = stateZip.split(' ');
        const zip = (stateZipParts.pop() || '').trim();
        const stateName = stateZipParts.join(' ').trim();
        const state = (getState(stateName)?.code || '');

        return { street, city, state, zip, country };
    },
    'place': (addressText: string = ''): IAddress => {
        // eg Leicester, Vermont, United States
        const [city, state, country] = addressText.split(',');

        const stateCode = getState(state)?.code || '';

        return {
            city: (city || '').trim(),
            state : stateCode,
            country: (country || '').trim()
        };
    },
    'neighborhood': (addressText: string = ''): IAddress => {
        // eg West End, Boston, Massachusetts, United States
        const [name, ...rest] = addressText.split(',');
        const address = placeTypeParsers.place(rest.join(','));
        return {
            ...address,
            name: name.trim()
        };
    },
    'poi': (addressText: string = ''): IAddress => {
        // eg McDonald's, 720 Cleveland St, Rapid City, South Dakota 57701, United States
        const [name, ...rest] = addressText.split(',');
        const address = placeTypeParsers.address(rest.join(','));
        return {
            ...address,
            name: name.trim()
        };
    }
};

export class LocationSearchService {
    static inject = () => [MapViewModel, EnvConfiguration];
    constructor(private map: MapViewModel, private config: EnvConfiguration){}

    private parseAddress = ({ place_name, place_type } : { place_name: string, place_type: string[] }): IAddress => {
        if (place_type.includes('poi')){
            return placeTypeParsers.poi(place_name);
        }
        if (place_type.includes('neighborhood')) {
            return placeTypeParsers.neighborhood(place_name);
        }
        else if (place_type.includes('place') || place_type.includes('city')) {
            return placeTypeParsers.place(place_name);
        }
        else if (place_type.includes('country')){
            return placeTypeParsers.country(place_name);
        }
        else {
            return placeTypeParsers.address(place_name);
        }
    };

    private getProximity = () => {
        if (this.map.isInitialized() && !this.map.isInitialCenter()){
            // Bias the search results to the wherever the user last left the center of the map
            const mapCenter = this.map.getCenter();
            return `${mapCenter.lng},${mapCenter.lat}`;
        } else {
            // If this is the first search by a new user who has denied location permission, we don't have a useful map center.
            // So for this edge case, bias towards whatever location mapbox can infer from the ip address. This is likely inaccurate
            // and we have FUD about whether it's working as expected (because we have seen different results in staging v prod).
            // So note that in app tests, we avoid hitting this path by panning the map a bit before each test.
            return 'ip';
        }
    };

    reverseLookup = async ({lng, lat}: ICoordinate) => {
        const params = new URLSearchParams();
        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('country', 'us');
        params.set('limit', '1');
        params.set('types', 'place,poi,address,country');
        params.set('worldview', 'us');

        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?${params.toString()}`;

        const result = await fetch(url);
        const { features } = await result.json();

        if (!features?.length) {
            console.log('No features returned by mapbox', {lat, lng});
            return undefined;
        }

        const { center, place_name, place_type } = features[0];
        const address = this.parseAddress({ place_name, place_type });

        // ignore if not US
        if (address.country !== 'United States') {
            console.log(`mapbox returned other country: "${place_name}"`, {lat, lng});
            return undefined;
        }

        return {
            address,
            coordinates: {
                lng: center[0],
                lat: center[1]
            }
        };
    };

    search = async ({ searchText, matchesToReturn }: { searchText: string, matchesToReturn: number }): Promise<ILocation[]> => {
        if (!searchText) {
            return [];
        }

        const params = new URLSearchParams();
        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('country', 'us');
        params.set('limit', String(matchesToReturn)); // max 10
        params.set('types', 'place,poi,address');
        params.set('worldview', 'us');
        params.set('proximity', this.getProximity());

        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchText}.json?${params.toString()}`;

        const result = await fetch(url);
        const { features } = await result.json();

        if (!features?.length) {
            console.log('No features returned by mapbox', {searchText});
            return [];
        }

        return features.map(({ center, place_name, place_type }: { center: number[], place_name: string, place_type: string[] }) => {
            return {
                address: this.parseAddress({ place_name, place_type }),
                coordinates: {
                    lng: center[0],
                    lat: center[1]
                }
            };
        });
    };

    suggest = async ({ searchText, matchesToReturn, sessionToken }: { searchText: string, matchesToReturn: number, sessionToken: string }): Promise<ISuggestion[]> => {
        if (!searchText) {
            return [];
        }

        const params = new URLSearchParams();

        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('session_token', sessionToken);
        params.set('q', searchText);
        params.set('country', 'us');
        params.set('limit', String(matchesToReturn)); // max 10
        params.set('types', 'address,street,city,neighborhood,poi');
        params.set('poi_category', INCLUDED_CATEGORIES.join(','));
        params.set('proximity', this.getProximity());

        const url = `https://api.mapbox.com/search/searchbox/v1/suggest?${params.toString()}`;

        const result = await fetch(url);
        const { suggestions } = await result.json();

        if (!suggestions?.length) {
            console.log('No suggestions returned by mapbox', {searchText});
            return [];
        }

        return suggestions.filter(Boolean).map(({ mapbox_id, feature_type, name, place_formatted, full_address }: { mapbox_id: string, feature_type: string, name: string, place_formatted: string, full_address: string }) => {
            const place_type = [feature_type];
            const place_name = (feature_type === 'address' ? full_address : feature_type === 'poi' ? `${name}, ${full_address}` : `${name}, ${place_formatted}`);
            return {
                address: this.parseAddress({ place_name, place_type }),
                id: mapbox_id
            };
        });
    };

    retrieve = async ({ id, sessionToken }: { id: string, sessionToken: string }): Promise<ICoordinate> => {
        const params = new URLSearchParams();
        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('session_token', sessionToken);

        const url = `https://api.mapbox.com/search/searchbox/v1/retrieve/${id}?${params.toString()}`;

        const result = await fetch(url);
        const { features } = await result.json();

        if (features?.length !== 1) {
            throw new Error(`Expected /retrieve to return 1 feature, but got ${features?.length || 0}`);
        }

        const center = features[0].geometry.coordinates;
        return {
            lng: center[0],
            lat: center[1]
        };
    };
}
